import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Pencil, ExternalLink, Inbox } from "lucide-react";
import { AdminShell } from "./AdminShell";
import { adminApi, mediaUrl } from "./adminApi";
import { fmtPrice } from "../api";

const STATUS = {
  pending: { label: "En attente de validation", cls: "bg-amber-500/20 text-amber-300" },
  published: { label: "Publié", cls: "bg-emerald-500/20 text-emerald-300" },
  refused: { label: "Refusé", cls: "bg-red-500/20 text-red-300" },
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }); } catch (_) { return ""; } };

export default function Submissions({ embedded } = {}) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const [members, setMembers] = useState([]);
  const [assign, setAssign] = useState({}); // sub_id -> owner_id

  const load = () => adminApi.listSubmissions().then(setItems).catch(() => setItems([]));
  useEffect(() => {
    load();
    adminApi.listMembers("approved").then((d) => setMembers(Array.isArray(d) ? d : [])).catch(() => setMembers([]));
  }, []);

  const publish = async (id) => { setBusy(id); try { await adminApi.publishSubmission(id, assign[id] || ""); await load(); } finally { setBusy(null); } };
  const refuse = async (id) => { setBusy(id); try { await adminApi.refuseSubmission(id); await load(); } finally { setBusy(null); } };

  return (
    <AdminShell embedded={embedded} title="Biens proposés">
      {items === null ? (
        <p className="text-zinc-500 text-sm">Chargement…</p>
      ) : items.length === 0 ? (
        <div className="text-center py-20 text-zinc-500" data-testid="submissions-empty">
          <Inbox className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
          <p className="text-sm">Aucun bien proposé pour le moment.</p>
        </div>
      ) : (
        <div className="border border-white/10 divide-y divide-white/5" data-testid="submissions-list">
          {items.map((s) => {
            const st = STATUS[s.status] || STATUS.pending;
            return (
              <div key={s.id} data-testid={`submission-row-${s.id}`} className="flex flex-col lg:flex-row lg:items-center gap-4 p-4 hover:bg-white/5 transition-colors">
                {s.cover ? (
                  <img src={mediaUrl(s.cover)} alt="" className="w-24 h-16 object-cover bg-white/5 shrink-0" />
                ) : (
                  <div className="w-24 h-16 bg-white/5 shrink-0 flex items-center justify-center text-zinc-700"><Inbox className="w-5 h-5" /></div>
                )}
                <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1">
                  <div className="min-w-0"><span className="text-[10px] uppercase tracking-wider text-zinc-500 block">Propriétaire</span><span className="text-sm truncate block">{s.owner_prenom} {s.owner_nom}</span></div>
                  <div className="min-w-0"><span className="text-[10px] uppercase tracking-wider text-zinc-500 block">Commune</span><span className="text-sm truncate block">{s.city || "—"}</span></div>
                  <div className="min-w-0"><span className="text-[10px] uppercase tracking-wider text-zinc-500 block">Type</span><span className="text-sm truncate block">{s.type || "—"}</span></div>
                  <div className="min-w-0"><span className="text-[10px] uppercase tracking-wider text-zinc-500 block">Prix</span><span className="text-sm text-[#C19A6B] truncate block">{fmtPrice(s.price)}</span></div>
                </div>
                <div className="flex flex-col items-start gap-1 shrink-0">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 ${st.cls}`}>{st.label}</span>
                  <span className="text-[11px] text-zinc-500">{fmtDate(s.created_at)}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {s.status === "published" && s.published_slug ? (
                    <a href={`/bien/${s.published_slug}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs uppercase tracking-wider border border-white/10 px-3 py-2 text-zinc-300 hover:text-[#C19A6B]"><ExternalLink className="w-3.5 h-3.5" /> Voir</a>
                  ) : (
                    <>
                      <select value={assign[s.id] || ""} onChange={(e) => setAssign((a) => ({ ...a, [s.id]: e.target.value }))} data-testid={`submission-assign-${s.id}`} title="Affecter à un apporteur / conseiller" className="text-xs bg-[#0d0d10] border border-white/15 rounded-[5px] px-2 py-2 text-white focus:outline-none focus:border-[#C19A6B] max-w-[170px]">
                        <option value="" className="bg-[#0d0d10]">Sans affectation</option>
                        {members.map((m) => <option key={m.member_id} value={m.member_id} className="bg-[#0d0d10]">{m.display_name}{m.account_type ? ` · ${m.account_type}` : ""}</option>)}
                      </select>
                      <Link to={`/admin/submissions/${s.id}/edit`} data-testid={`submission-edit-${s.id}`} className="flex items-center gap-1.5 text-xs uppercase tracking-wider border border-white/10 px-3 py-2 text-zinc-300 hover:text-[#C19A6B] hover:border-[#C19A6B]/40"><Pencil className="w-3.5 h-3.5" /> Modifier</Link>
                      <button disabled={busy === s.id} onClick={() => publish(s.id)} data-testid={`submission-publish-${s.id}`} className="flex items-center gap-1.5 text-xs uppercase tracking-wider bg-emerald-500/90 hover:bg-emerald-500 text-white px-3 py-2 disabled:opacity-50"><Check className="w-3.5 h-3.5" /> {assign[s.id] ? "Valider & affecter" : "Publier"}</button>
                      {s.status !== "refused" && (
                        <button disabled={busy === s.id} onClick={() => refuse(s.id)} data-testid={`submission-refuse-${s.id}`} className="flex items-center gap-1.5 text-xs uppercase tracking-wider border border-red-400/30 text-red-300 hover:bg-red-500/10 px-3 py-2 disabled:opacity-50"><X className="w-3.5 h-3.5" /> Refuser</button>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminShell>
  );
}
